import { create } from 'zustand';
import type { User } from '../api/types';

interface AuthState {
  token: string | null;
  user: User | null;
  sessionExpired: boolean;
  setSession: (token: string, user: User, rememberMe: boolean) => void;
  setUser: (user: User) => void;
  logout: () => void;
  expireSession: () => void;
  clearSessionExpired: () => void;
}

const STORAGE_KEY = 'wctyper-auth';

interface PersistedShape {
  token: string;
  user: User;
}

/**
 * Reads the persisted session from localStorage first (rememberMe = true) and
 * sessionStorage as a fallback (rememberMe = false). On rememberMe = false the
 * payload lives only as long as the browser window — closing the tab logs out.
 */
function readPersisted(): PersistedShape | null {
  try {
    const fromLocal = window.localStorage.getItem(STORAGE_KEY);
    if (fromLocal) return JSON.parse(fromLocal) as PersistedShape;
    const fromSession = window.sessionStorage.getItem(STORAGE_KEY);
    if (fromSession) return JSON.parse(fromSession) as PersistedShape;
  } catch { /* corrupted blob — fall through to logged-out state */ }
  return null;
}

function writePersisted(payload: PersistedShape, rememberMe: boolean) {
  const json = JSON.stringify(payload);
  if (rememberMe) {
    window.localStorage.setItem(STORAGE_KEY, json);
    window.sessionStorage.removeItem(STORAGE_KEY);
  } else {
    window.sessionStorage.setItem(STORAGE_KEY, json);
    window.localStorage.removeItem(STORAGE_KEY);
  }
}

function clearPersisted() {
  window.localStorage.removeItem(STORAGE_KEY);
  window.sessionStorage.removeItem(STORAGE_KEY);
}

const initial: PersistedShape | null =
  typeof window === 'undefined' ? null : readPersisted();

export const useAuth = create<AuthState>()((set) => ({
  token: initial?.token ?? null,
  user: initial?.user ?? null,
  sessionExpired: false,
  setSession: (token, user, rememberMe) => {
    writePersisted({ token, user }, rememberMe);
    set({ token, user, sessionExpired: false });
  },
  setUser: (user) => {
    // Persist the refreshed profile under whichever storage already holds the token.
    const inLocal = !!window.localStorage.getItem(STORAGE_KEY);
    const token = useAuth.getState().token;
    if (token) writePersisted({ token, user }, inLocal);
    set({ user });
  },
  logout: () => {
    clearPersisted();
    set({ token: null, user: null, sessionExpired: false });
  },
  expireSession: () => {
    clearPersisted();
    set({ token: null, user: null, sessionExpired: true });
  },
  clearSessionExpired: () => set({ sessionExpired: false }),
}));

export function isAdmin(user: User | null): boolean {
  return user?.role === 'ADMIN';
}
